/**
 * Compress an image file using the browser Canvas API.
 * Resizes to a maximum width while preserving aspect ratio,
 * and re-encodes as JPEG to reduce file size and strip EXIF data.
 *
 * The canvas is filled with `background` (default white) before the
 * source is drawn, so transparent PNGs land on a known backdrop instead
 * of relying on whatever surface they're rendered against later. JPEG
 * has no alpha channel anyway, so the fill is necessary to avoid the
 * transparent regions decoding as black on some browsers.
 *
 * @param {File} file - The source image file
 * @param {object} [options]
 * @param {number} [options.maxWidth=1024]
 * @param {number} [options.quality=0.85]
 * @param {string} [options.background='#ffffff']
 * @returns {Promise<Blob>} The compressed image as a JPEG Blob
 */
export async function compressImage(file, options = {}) {
  // Backwards-compatible signature: existing callers pass (file, maxWidth, quality)
  // as positionals; new callers use the options object.
  const {
    maxWidth = 1024,
    quality = 0.85,
    background = '#ffffff',
  } = typeof options === 'number'
    ? { maxWidth: options, quality: arguments[2] }
    : options

  if (!file || !file.type.startsWith('image/')) {
    throw new Error('Not an image file')
  }

  const dataUrl = await readFileAsDataURL(file)
  const img = await loadImage(dataUrl)

  // Calculate target dimensions (downsize only, never upscale)
  const scale = img.width > maxWidth ? maxWidth / img.width : 1
  const targetWidth = Math.round(img.width * scale)
  const targetHeight = Math.round(img.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight

  const ctx = canvas.getContext('2d')
  if (background) {
    ctx.fillStyle = background
    ctx.fillRect(0, 0, targetWidth, targetHeight)
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Failed to compress image'))
      },
      'image/jpeg',
      quality,
    )
  })
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })
}
